import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'
import { uploadFile } from '../api/storage'
import type { Post } from '../types'

interface ExportBarProps {
  post: Post
}

const THEMES = ['default', 'minimal', 'technical', 'business', 'arkturian'] as const

export function ExportBar({ post }: ExportBarProps) {
  const [theme, setTheme] = useState('business')
  const [coverMode, setCoverMode] = useState<'starburst' | 'p5js' | 'custom' | 'none'>('starburst')
  const [starburstTheme, setStarburstTheme] = useState('geometric')
  const [designId, setDesignId] = useState('M_1_5_03')
  const [coverLabel, setCoverLabel] = useState('')
  const [exporting, setExporting] = useState(false)
  const [showCoverConfig, setShowCoverConfig] = useState(false)
  const [smartBreaks, setSmartBreaks] = useState(true)
  const queryClient = useQueryClient()

  const { data: logoPresets = [] } = useQuery({
    queryKey: ['logo-presets'],
    queryFn: () => apiClient.getLogos(),
    staleTime: 300_000,
  })

  const activeLogos = (post.logo_url || '').split(',').map(u => u.trim()).filter(Boolean)

  const toggleLogo = async (url: string) => {
    let updated: string[]
    if (activeLogos.includes(url)) {
      updated = activeLogos.filter(u => u !== url)
    } else {
      updated = [...activeLogos, url]
    }
    const newLogoUrl = updated.join(',') || null
    await apiClient.updatePost(post.id, { logo_url: newLogoUrl })
    queryClient.invalidateQueries({ queryKey: ['post', String(post.id)] })
  }

  const clearLogos = async () => {
    await apiClient.updatePost(post.id, { logo_url: null })
    queryClient.invalidateQueries({ queryKey: ['post', String(post.id)] })
  }

  // Custom logo upload — pushes user-supplied image to storage-api
  // and adds it to the post.logo_url comma-separated list. Reuses the
  // same multi-logo render path the presets go through, so the new
  // logo appears as a regular thumb alongside the presets after the
  // post-query revalidates.
  const logoFileInputRef = useRef<HTMLInputElement | null>(null)
  const [uploadingLogo, setUploadingLogo] = useState(false)

  // Custom cover-image upload — uploads to storage-api and writes
  // the URL to post.cover_image_url, which the PDF export reads when
  // coverMode='custom'. Also auto-switches the mode to 'custom' so
  // the just-uploaded image is what's actually used.
  const coverFileInputRef = useRef<HTMLInputElement | null>(null)
  const [uploadingCover, setUploadingCover] = useState(false)

  const handleCoverFile = async (file: File) => {
    setUploadingCover(true)
    try {
      const { url } = await uploadFile(file, {
        isPublic: true,
        permanent: true,
        context: `post-${post.id}-cover`,
      })
      await apiClient.updatePost(post.id, { cover_image_url: url })
      queryClient.invalidateQueries({ queryKey: ['post', String(post.id)] })
      setCoverMode('custom')
      setPreviousCoverMode('custom')
    } catch (err) {
      alert(`Cover-Upload fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setUploadingCover(false)
    }
  }

  const handleLogoFile = async (file: File) => {
    setUploadingLogo(true)
    try {
      const { url } = await uploadFile(file, {
        isPublic: true,
        permanent: true,
        context: `post-${post.id}-logo`,
      })
      const updated = [...activeLogos, url]
      await apiClient.updatePost(post.id, { logo_url: updated.join(',') })
      queryClient.invalidateQueries({ queryKey: ['post', String(post.id)] })
    } catch (err) {
      alert(`Logo-Upload fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setUploadingLogo(false)
    }
  }

  // Explicit cover-on/off toggle (Alex 2026-05-28 — the "none" entry
  // hidden in the cover-mode dropdown was easy to miss). When the user
  // turns the toggle off, we remember the last cover mode so flipping
  // back restores their previous design choice instead of resetting
  // to 'starburst'.
  const [previousCoverMode, setPreviousCoverMode] = useState<
    'starburst' | 'p5js' | 'custom'
  >('starburst')
  const coverEnabled = coverMode !== 'none'
  const toggleCoverEnabled = () => {
    if (coverEnabled) {
      // Switching OFF — remember current mode for next toggle. The
      // outer `coverEnabled` check already proved coverMode !== 'none',
      // so TS narrows it to the cover-active union here.
      setPreviousCoverMode(coverMode as 'starburst' | 'p5js' | 'custom')
      setCoverMode('none')
    } else {
      // Switching ON — restore the remembered mode.
      setCoverMode(previousCoverMode)
    }
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const blob = await apiClient.exportPostThemedPdf(post.id, {
        theme,
        logoUrl: post.logo_url || undefined,
        cover: coverMode !== 'none',
        starburstTheme: coverMode === 'starburst' ? starburstTheme : undefined,
        designId: coverMode === 'p5js' ? designId : undefined,
        coverImageUrl: coverMode === 'custom' ? (post.cover_image_url || undefined) : undefined,
        coverLabel: coverLabel || undefined,
        smartBreaks: smartBreaks ? undefined : false,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${post.slug || `post-${post.id}`}-${theme}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (err) {
      alert(`Export failed: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="export-bar-container">
      {/* Row 1: Theme buttons + Generate */}
      <div className="export-bar">
        <span className="export-bar-label">
          <i className="fas fa-file-pdf" style={{ marginRight: 4 }} /> PDF
        </span>
        <div className="export-theme-buttons">
          {THEMES.map(t => (
            <button
              key={t}
              className={`export-theme-btn ${theme === t ? 'active' : ''}`}
              onClick={() => setTheme(t)}
            >
              {t}
            </button>
          ))}
        </div>
        <button
          className="export-config-btn"
          onClick={() => setShowCoverConfig(!showCoverConfig)}
          title="Cover & Logo Einstellungen"
        >
          <i className="fas fa-cog" />
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer' }}>
          <input type="checkbox" checked={smartBreaks} onChange={(e) => setSmartBreaks(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
          Smart Breaks
        </label>
        <button
          className="generate-btn"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? <><i className="fas fa-spinner fa-spin" /> Generating...</> : 'Generate PDF'}
        </button>
      </div>

      {/* Row 2: Cover + Logo config (collapsible) */}
      {showCoverConfig && (
        <div className="export-config">
          <div className="export-config-section">
            <span className="export-config-label">Cover</span>
            {/* Explicit on/off toggle — easier than finding "None" in the dropdown */}
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                marginRight: 6,
                cursor: 'pointer',
              }}
              title="Deckblatt im Export an- oder ausschalten"
            >
              <input
                type="checkbox"
                checked={coverEnabled}
                onChange={toggleCoverEnabled}
                style={{ accentColor: 'var(--accent)' }}
              />
              <span>Deckblatt</span>
            </label>
            <select
              value={coverMode === 'none' ? previousCoverMode : coverMode}
              onChange={(e) => {
                const v = e.target.value as 'starburst' | 'p5js' | 'custom'
                setCoverMode(v)
                setPreviousCoverMode(v)
              }}
              disabled={!coverEnabled}
              style={!coverEnabled ? { opacity: 0.4 } : undefined}
            >
              <option value="starburst">Starburst</option>
              <option value="p5js">Generative Art</option>
              <option value="custom">Custom</option>
            </select>
            {coverMode === 'starburst' && (
              <select value={starburstTheme} onChange={(e) => setStarburstTheme(e.target.value)}>
                <option value="geometric">Geometric</option>
                <option value="minimal">Minimal</option>
                <option value="vibrant">Vibrant</option>
                <option value="organic">Organic</option>
                <option value="dark_elegant">Dark Elegant</option>
                <option value="neon">Neon</option>
                <option value="mandala">Mandala</option>
                <option value="lotus">Lotus</option>
              </select>
            )}
            {coverMode === 'p5js' && (
              <select value={designId} onChange={(e) => setDesignId(e.target.value)}>
                <optgroup label="Flow">
                  <option value="M_1_5_02">Perlin Flow 2D</option>
                  <option value="M_1_5_03">Perlin Flow 3D</option>
                  <option value="M_1_5_04">Perlin Ribbons</option>
                </optgroup>
                <optgroup label="Radial">
                  <option value="P_2_0_01">Circle Lines</option>
                  <option value="P_2_0_02">Polygon</option>
                </optgroup>
                <optgroup label="Grid">
                  <option value="P_2_1_1_01">Diagonals</option>
                  <option value="P_2_1_1_04">Rotating Shapes</option>
                  <option value="P_2_1_2_01">Circles</option>
                  <option value="P_2_1_3_01">Concentric</option>
                </optgroup>
              </select>
            )}
            {coverMode === 'custom' && coverEnabled && (
              <>
                {post.cover_image_url && (
                  <img
                    src={post.cover_image_url}
                    alt="Cover-Vorschau"
                    title="Aktuelles Cover-Bild"
                    style={{
                      height: 32,
                      borderRadius: 4,
                      border: '1px solid var(--border, #ccc)',
                      objectFit: 'cover',
                    }}
                  />
                )}
                <button
                  type="button"
                  onClick={() => coverFileInputRef.current?.click()}
                  disabled={uploadingCover}
                  title="Bild als Deckblatt hochladen"
                  style={{
                    padding: '4px 10px',
                    fontSize: 11,
                    borderRadius: 4,
                    border: '1px dashed var(--border, #ccc)',
                    background: 'transparent',
                    cursor: uploadingCover ? 'wait' : 'pointer',
                  }}
                >
                  {uploadingCover ? (
                    <>
                      <i className="fas fa-spinner fa-spin" style={{ marginRight: 4 }} />
                      Lade hoch …
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload" style={{ marginRight: 4 }} />
                      {post.cover_image_url ? 'Bild ändern' : 'Bild hochladen'}
                    </>
                  )}
                </button>
                <input
                  ref={coverFileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) void handleCoverFile(file)
                    e.target.value = ''
                  }}
                />
              </>
            )}
            {coverMode !== 'none' && (
              <input
                type="text" value={coverLabel} onChange={(e) => setCoverLabel(e.target.value)}
                placeholder="Label (z.B. Proposal)" style={{ maxWidth: 140 }}
              />
            )}
          </div>
          <div className="export-config-section">
            <span className="export-config-label">Logos</span>
            <div className="export-logo-grid">
              {logoPresets.map((logo) => (
                <img
                  key={logo.url}
                  src={logo.url}
                  alt={logo.label}
                  title={logo.label}
                  className={`export-logo-thumb ${activeLogos.includes(logo.url) ? 'active' : ''}`}
                  onClick={() => toggleLogo(logo.url)}
                />
              ))}
              {/* Custom logos attached via upload that don't match any
                  preset still show up here, since `activeLogos` carries
                  all entries from post.logo_url. We render any URL that's
                  NOT in the preset list as its own thumb so the user
                  sees what they uploaded. */}
              {activeLogos
                .filter((u) => !logoPresets.some((p) => p.url === u))
                .map((url) => (
                  <img
                    key={url}
                    src={url}
                    alt="Eigenes Logo"
                    title="Eigenes Logo (Klick zum Entfernen)"
                    className="export-logo-thumb active"
                    onClick={() => toggleLogo(url)}
                  />
                ))}
              {/* Upload-Button */}
              <button
                type="button"
                onClick={() => logoFileInputRef.current?.click()}
                disabled={uploadingLogo}
                title="Eigenes Logo hochladen"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 4,
                  border: '1px dashed var(--border, #ccc)',
                  background: 'transparent',
                  cursor: uploadingLogo ? 'wait' : 'pointer',
                  color: 'var(--text-muted, #888)',
                  fontSize: 14,
                  padding: 0,
                }}
              >
                {uploadingLogo ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className="fas fa-plus" />
                )}
              </button>
              <input
                ref={logoFileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) void handleLogoFile(file)
                  // Reset so re-selecting the same file fires onChange
                  e.target.value = ''
                }}
              />
              {activeLogos.length > 0 && (
                <button onClick={clearLogos} className="export-logo-clear">
                  <i className="fas fa-times" /> clear
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
